import {
  CardDataWithHeroExtras,
  BadgeConfig,
  CardData,
} from 'flows/core/types/cards';
import { EssentialCards } from 'flows/core/types/layouts';
import { ItemKinds } from 'types';
import { ItemStatuses } from 'constants/statuses';
import { DataForCards } from 'flows/core/hooks/use-data-for-cards';
import sortBy from 'lodash/sortBy';
import { dollyMove } from 'types/generated/dollyMove';
import {
  enabledItemDefOfKind,
  itemOfKind,
  Badges,
  lookupImportanceByCardStatus,
} from './utils';

const mysPromoDraftMap = '/images/mys-promo-draft-map.svg';
const mysPromoMap = '/images/mys-promo-map.svg';

const KIND = ItemKinds.MOVE_YOUR_STUFF;

const MYS_STATIC_DATA = {
  subtype: 'IMAGE',
  icon: 'TRUCK',
  completionTimeText: '< 1 min',
} as const;

const MYS_INITIATE_PROMO_DATA = {
  ...MYS_STATIC_DATA,
  title: 'Move your stuff',
  image: {
    url: mysPromoMap,
    alt: 'mys-initiate-promo',
  },
};

const MYS_DRAFT_PROMO_DATA = {
  ...MYS_STATIC_DATA,
  title: 'Finish booking movers',
  image: {
    url: mysPromoDraftMap,
    alt: 'mys-draft-promo',
  },
} as const;

const MYS_HERO_EXTRAS = {
  initiate: {
    heading: 'Get help moving a couch, or your whole home.',
    ctaText: 'Book movers',
  },
  draft: {
    heading: 'So close! Get your movers lined up.',
    ctaText: 'Book movers',
  },
} as const;

export const statusToImportance = (status?: string) => {
  if (status === 'started') {
    return 1_500_001;
  }
  if (status === 'completed') {
    return 0;
  }
  return 750_000;
};

export const statusToBadge = (status?: string): BadgeConfig | undefined => {
  if (status === 'started' || status === 'pending') {
    return Badges.RECENTLY_VIEWED;
  }
  if (status === 'reserved') {
    return Badges.ALMOST_DONE;
  }
  if (status === 'cancelled') {
    return Badges.CANCELLED;
  }
  if (status === 'completed' || status === 'validating') {
    return Badges.DONE;
  }
  return undefined;
};

// @ts-expect-error FIXME: TS7031 - strict mode migration
const makeDraftMovingCards = ({ items }) => {
  const mysItems = sortBy(items.filter(itemOfKind(KIND)), 'createdAt');
  // @ts-expect-error FIXME: TS2322 - strict mode migration
  const mysDraftCards: CardData[] = mysItems
    .map((item) => {
      if (['completed', 'validating'].includes(item.status)) {
        return undefined;
      }
      return {
        type: 'PROMOTED' as const,
        data: {
          ...MYS_DRAFT_PROMO_DATA,
          action: {
            type: 'internal' as const,
            route: `/move-your-stuff/resume/${item.id}`,
          },
          extraContent: [
            {
              type: 'AddressLine' as const,
              content: {
                point: 'From',
                address: item.metadata?.from_address?.split(',')[0],
              },
              key: 'fromAddress',
            },
            {
              type: 'AddressLine' as const,
              content: {
                point: 'To',
                address: item.metadata?.to_address?.split(',')[0],
              },
              key: 'toAddress',
            },
          ],
          badge: statusToBadge(item.status),
        },
        identifier: `dolly_draft_${item.id}`,
        importanceScore: lookupImportanceByCardStatus('MYS', item.status),
      };
    })
    .filter(Boolean);
  return mysDraftCards;
};

export const makeTodayTabMoveYourStuffCards = (
  { itemDefinitions, items }: DataForCards,
  dolly?: dollyMove,
  miniMovesEnabled?: boolean
): CardDataWithHeroExtras[] => {
  const itemDef = itemDefinitions.find(enabledItemDefOfKind(KIND));
  const moveYourStuffItems = items.filter(itemOfKind(KIND));

  const isEligible = miniMovesEnabled || dolly?.dollyMove?.eligible;

  if (itemDef && isEligible) {
    if (moveYourStuffItems.length === 0) {
      return [
        {
          type: 'PROMOTED',
          data: {
            ...MYS_INITIATE_PROMO_DATA,
            action: {
              type: 'internal',
              route: '/moving',
            },
          },
          heroExtras: MYS_HERO_EXTRAS.initiate,
          identifier: 'mys_promoted',
          // @ts-expect-error FIXME: TS2345 - strict mode migration
          importanceScore: statusToImportance(moveYourStuffItems[0]?.status),
        },
      ];
    }
    return makeDraftMovingCards({ items }).map((mysDraftCard) => ({
      ...mysDraftCard,
      heroExtras: MYS_HERO_EXTRAS.draft,
    }));
  }
  return [];
};

export const makeMovingTabDraftMoveYourStuffCards = ({
  items,
}: DataForCards): CardData[] => {
  return makeDraftMovingCards({ items });
};

const MYS_ESSENTIALS_STATIC_DATA = {
  subtype: 'MYS' as const,
  icon: 'TRUCK' as const,
  title: 'Move your stuff',
  description: 'Compare movers and book your move',
  completionTimeText: '< 1 min',
};

export const makeEssentialsTabMoveYourStuffCards = (
  { itemDefinitions, items }: DataForCards,
  miniMovesEnabled?: boolean
): EssentialCards => {
  if (!miniMovesEnabled) {
    return { toDoCards: [], completedCards: [] };
  }

  const itemDef = itemDefinitions.find(enabledItemDefOfKind(KIND));
  const moveYourStuffItems = items.filter(itemOfKind(KIND));
  const toDoCards: CardData[] = [];
  const completedCards: CardData[] = [];

  if (itemDef) {
    if (moveYourStuffItems.length === 0) {
      toDoCards.push({
        type: 'INITIATE',
        data: {
          ...MYS_ESSENTIALS_STATIC_DATA,
          action: {
            type: 'internal',
            route: '/moving',
          },
          status: ItemStatuses.NOT_STARTED,
        },
        identifier: 'move_your_stuff',
        importanceScore: 750_000,
      });
    } else {
      moveYourStuffItems.forEach((item) => {
        const status = item.status ?? undefined;
        const badge = statusToBadge(status);
        if (['completed', 'validating'].includes(item.status ?? '')) {
          completedCards.push({
            type: 'INITIATE',
            data: {
              ...MYS_ESSENTIALS_STATIC_DATA,
              action: {
                type: 'internal',
                route: '/moving',
              },
              badge,
              status,
              ctaText: badge?.variant === 'done' ? 'View task' : undefined,
            },
            identifier: `move_your_stuff_completed_${item.id}`,
            importanceScore: 750_000,
          });
        } else {
          toDoCards.push({
            type: 'INITIATE',
            data: {
              ...MYS_ESSENTIALS_STATIC_DATA,
              action: {
                type: 'internal',
                route: '/moving',
              },
              badge,
              status,
            },
            identifier: `move_your_stuff_${item.id}`,
            importanceScore: lookupImportanceByCardStatus('MYS', status),
          });
        }
      });
    }
  }

  return { toDoCards, completedCards };
};
